import React from 'react';
import ReactDOM from 'react-dom';
import {ToastContainer} from 'react-toastify';

import * as Sentry from '@sentry/react';

import Router from 'Components/Router';
import {Loading} from 'Components/Partials';

import User from 'Services/User';
import Settings from 'Services/Settings';
import Auth from 'Services/Api/Auth/Auth';
import Me from 'Services/Api/Me/Me';
import TwoFactor from 'Services/Api/Me/TwoFactor';
import Teams from 'Services/Api/Teams/Teams';
import {Event, Modal} from 'Services';
import SiteConfigApi from "Services/Api/SiteConfig/SiteConfig";

import 'react-toastify/dist/ReactToastify.min.css';
import SurveyPopUp from "Components/Partials/Modals/SurveyPopUp";
import EventStart from "Components/Partials/Modals/EventStart";
import OfferPopUp from "Components/Partials/Modals/OfferPopUp";
import {Socket} from "../Services";

export default class App extends React.Component {
    /**
     * @var state
     * @type {{loading: boolean}}
     */
    state = {
        loading: true,
        redirect: null,
        deepLink: null
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        await Settings.refresh();

        this.loadUser();

        Auth.on('login', this.fetchUser);
        Auth.on('logout', this.logoutUser);

        Auth.on('login', this.refreshSettingsWithAlert);
        Auth.on('logout', this.refreshSettingsWithAlert);

        Auth.on('login', this.handleOpenOfferModal);

        if (window.base.features.allow_profile_change) {
            Me.on('update', User.refresh);
        }

        if (window.base.features.avatar) {
            Me.on('update-avatar', User.refresh);
        }

        if (window.base.features.delete_account) {
            Me.on('delete', this.logoutUser);
        }

        if (!window.base.features.verify_registrations) {
            Auth.on('register', this.fetchUser);
        }

        if (window.base.features.teams) {
            Me.on('update-team', User.refresh);
            Teams.on('updated', User.refresh);
        }

        if (window.base.features.two_factor) {
            TwoFactor.on('disable', User.refresh);
            TwoFactor.on('enable', User.refresh);

            Auth.on('two-factor', User.refresh);
        }

        await this.fetchPopups();

        this.subscribeToPopUps();
    };

    refreshSettingsWithAlert = () => {
        Settings.refresh(true);
    }

    subscribeToPopUps = () => {
        Socket.getConnection()
            .private('site_config')
            .listen('SiteConfigUpdated', (e) => {
                if (e.siteConfig.key === 'event_start_pop_up') {
                    if (e.siteConfig.value === 'show' && !window.location.pathname.startsWith('/main-stage') && !localStorage.getItem('event-started')) {
                        this.handleOpenEventStartModal();
                    }
                }
                if (e.siteConfig.key === 'survey_pop_up') {
                    if (
                        e.siteConfig.value === 'show' &&
                        window.location.pathname !== '/survey' &&
                        window.location.pathname !== '/main-stage/chat' &&
                        window.location.pathname !== '/main-stage/questions' &&
                        !localStorage.getItem('survey')
                    ) {
                        this.handleOpenSurveyModal();
                    }
                }

                // this.handlePhaseChange(parseInt(e.siteConfig.value));
            });
    }

    /**
     * @method fetchSurveyConfig
     * @return {Promise<void>}
     */
    fetchPopups = async () => {
        const response = await SiteConfigApi.get(null);

        const showEventStart = response.data.data.find((item) => item.key === 'event_start_pop_up')?.value ?? 'hide';
        const showSurvey = response.data.data.find((item) => item.key === 'survey_pop_up')?.value ?? 'hide';

        if (showEventStart === 'show' && !window.location.pathname.startsWith('/main-stage') && !localStorage.getItem('event-started')) {
            this.handleOpenEventStartModal();
        }

        if (showSurvey === 'show' && window.location.pathname !== '/survey' && !localStorage.getItem('survey')) {
            this.handleOpenSurveyModal();
        }
    };

    /**
     * @method handleOpenUserRedirectModal
     */
    handleOpenSurveyModal = () => {
        Modal.open({
            component:  SurveyPopUp,
            props: this.props.props ?? {}
        });
    };

    /**
     * @method handleOpenEventStartModal
     */
    handleOpenEventStartModal = () => {
        Modal.open({
            component:  EventStart,
            props: this.props.props ?? {}
        });
    };

    /**
     * @method handleOpenOfferModal
     * Fetches the authenticated user's pending waitlist offer from the API.
     * Opens the OfferPopUp modal only if an offer exists — no localStorage used.
     */
    handleOpenOfferModal = async () => {
        const response = await Me.getPendingAttendanceOffer();
        const offer = response?.data?.offer;

        if (!offer) {
            return;
        }

        Modal.open({
            component: OfferPopUp,
            props: { offer },
        });
    };

    /**
     * @method loadUser
     * @return {Promise<void>}
     */
    loadUser = async () => {
        await User.refresh();

        let deepLink = null;

        if (User.loggedIn) {
            Event.on('api-connection-expired', this.logoutUser);

            // Check for a pending offer on initial page load (e.g. returning user).
            this.handleOpenOfferModal();
        } else if (window.location.pathname !== '' && window.location.pathname !== '/') {
            deepLink = window.location.pathname;
        }

        this.finishLoading(deepLink, null);
    }

    /**
     * @method fetchUser
     * @return {Promise<void>}
     */
    fetchUser = async () => {
        await User.refresh();

        Event.on('api-connection-expired', this.logoutUser);

        this.finishLoading(null, this.state.deepLink);
    };

    /**
     * @method finishLoading
     * @param {string} deepLink
     * @param {string} redirect
     */
    finishLoading = (deepLink = null, redirect = null) => {
        this.setState({
            loading: false,
            deepLink,
            redirect
        });
    };

    /**
     * @method logoutUser
     * @return {Promise<void>}
     */
    logoutUser = () => {
        Modal.emit('close-all');

        User.logout();

        Event.removeListener('api-connection-expired', this.logoutUser);

        this.finishLoading();
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        Auth.removeListener('login', this.fetchUser);
        Auth.removeListener('logout', this.logoutUser);

        Auth.removeListener('login', this.refreshSettingsWithAlert);
        Auth.removeListener('logout', this.refreshSettingsWithAlert);

        Auth.removeListener('login', this.handleOpenOfferModal);

        if (window.base.features.allow_profile_change) {
            Me.removeListener('update', User.refresh);
        }

        if (window.base.features.avatar) {
            Me.removeListener('update-avatar', User.refresh);
        }

        if (window.base.features.delete_account) {
            Me.removeListener('delete', this.logoutUser);
        }

        if (!window.base.features.verify_registrations) {
            Auth.removeListener('register', this.fetchUser);
        }

        if (window.base.features.teams) {
            Me.removeListener('update-team', User.refresh);
            Teams.removeListener('updated', User.refresh);
        }

        if (window.base.features.two_factor) {
            TwoFactor.removeListener('disable', User.refresh);
            TwoFactor.removeListener('enable', User.refresh);

            Auth.removeListener('two-factor', User.refresh);
        }

        //Socket.echo.leave('site_config');
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {loading, redirect} = this.state;

        return (
            <Sentry.ErrorBoundary beforeCapture={(scope) => {
                if (User.loggedIn) {
                    scope.setTag('user-id', User.id);
                }
            }}>
                {loading ? <Loading /> : <Router redirect={redirect} />}

                <ToastContainer
                    hideProgressBar={true}
                    closeButton={false}
                    autoClose={5000}
                />
            </Sentry.ErrorBoundary>
        );
    };
}

if (process.env.MIX_SENTRY) {
    Sentry.init({
        dsn: process.env.MIX_SENTRY,
        integrations: [],
        beforeSend(event, hint) {
            // Check if it is an exception, and if so, show the report dialog
            if (event.exception) {
                // Sentry.showReportDialog({ eventId: event.event_id });
            }
            return event;
        },
    });
}

ReactDOM.render(<App/>, document.getElementById('app'));
